// cfoCashTroughCopy — deterministic plain-English callouts for weeks whose
// projected ending cash crosses the min-cash floor. Names the two largest
// outflow categories and the single largest named item that week, e.g.
// "Week of Aug 31 dips to −$12.0K — Payroll ($38.0K) and the Hartford
// Insurance payment ($9.0K) land together."

import type { QuickBooksCashPlanningRow } from '../../../data/ServerApiClient'
import {
  signedCashAmount,
  treasurerRowKeyForPlanningRow,
  type CashTableModel,
  type CashTroughCallout,
} from './cfoCashTableModel'

const compact = (value: number): string => {
  const abs = Math.abs(value)
  const sign = value < 0 ? '−' : ''
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`
  return `${sign}$${abs.toFixed(0)}`
}

export function buildTroughCallouts(
  model: CashTableModel,
  rows: QuickBooksCashPlanningRow[],
): CashTroughCallout[] {
  const ending = model.lines.find((line) => line.key === 'ending')
  if (!ending) return []
  const callouts: CashTroughCallout[] = []
  ending.cells.forEach((cell, columnIndex) => {
    if (!cell.breachesFloor || cell.amount == null) return
    const column = model.columns[columnIndex]
    if (!column) return
    // Closed weeks are history — the callout narrates upcoming trouble only.
    if (column.status === 'closed') return
    // The two largest outflow categories that week, from the already-built cells.
    const outflows = model.lines
      .filter((line) => line.kind === 'outflow')
      .map((line) => ({ label: line.label, amount: line.cells[columnIndex]?.amount ?? 0 }))
      .filter((entry) => entry.amount < -0.005)
      .sort((a, b) => a.amount - b.amount)
      .slice(0, 2)
    // The single largest named outflow item that week, for a concrete noun.
    const namedItem = rows
      .filter((row) => {
        const inColumn = model.lines.some((line) => line.cells[columnIndex]?.rowIds.includes(String(row.id)))
        return inColumn && signedCashAmount(row) < 0 && Boolean(row.counterparty_name)
      })
      .sort((a, b) => signedCashAmount(a) - signedCashAmount(b))[0]
    const drivers = outflows.map((entry) => `${entry.label} (${compact(Math.abs(entry.amount))})`)
    const namedKey = namedItem ? treasurerRowKeyForPlanningRow(namedItem) : null
    const namedInDrivers = namedItem && outflows.some((entry) =>
      entry.label.toLowerCase().startsWith(String(namedKey || '').split('_')[0]))
    const namedClause = namedItem && !namedInDrivers
      ? ` — the ${namedItem.counterparty_name} payment (${compact(Math.abs(signedCashAmount(namedItem)))}) lands the same week`
      : ''
    const driversClause = drivers.length
      ? ` — ${drivers.join(' and ')} land together`
      : ''
    callouts.push({
      weekStart: column.weekStart,
      weekLabel: column.label,
      endingCash: cell.amount,
      message: `${column.label} dips to ${compact(cell.amount)}${driversClause || namedClause}.`,
    })
  })
  return callouts
}
